/**
 * Où atterrit un bloc lâché, traduit en « quel parent, devant quel frère ».
 *
 * *Demandé par Chris le 23/09/2026 : « je veux prendre les blocs par drag and drop pour
 * aller vite être précis ».*
 *
 * Sortie de l'écran parce que la règle de dépôt est le seul endroit où le geste peut être
 * FAUX sans se voir : un bloc qui atterrit un niveau trop haut ressemble beaucoup à un bloc
 * bien posé, et l'écart ne se lirait que sur les montants d'un chapitre, longtemps après.
 *
 * **La règle tient en deux lignes, et c'est délibéré.** Moitié HAUTE d'une ligne : le bloc
 * s'insère juste avant elle, à son niveau. Moitié BASSE : il entre dans le chapitre survolé
 * comme premier élément, ou se pose après la tâche survolée, à son niveau. Rien ne dépend
 * de la position horizontale du curseur — une indentation à viser se rate, et le produit a
 * déjà tranché plusieurs fois pour le geste qu'on ne peut pas manquer.
 *
 * Les trois cas que Chris a décrits se lisent directement dedans :
 *  - « le chapitre 1 après le chapitre 2.4 » — moitié haute de ce qui suit le bloc 2.4 ;
 *  - « le 1.4 entre le 3.1.3 et le 3.1.4 » — moitié haute du 3.1.4 ;
 *  - « le 2.7.2 entre le 2.3.2 et le 2.3.3 » — moitié haute du 2.3.3.
 */

/** Ce que l'écran sait d'un poste : de quoi retrouver un parent et une fratrie. */
export type NoeudPosable = {
  readonly nature: "chapitre" | "ouvrage";
  readonly numero: string;
  readonly enfants?: readonly NoeudPosable[];
};

/** La moitié de la ligne survolée. */
export type MoitieSurvolee = "haute" | "basse";

export type Pose = {
  /** Le chapitre d'accueil ; nul pour une racine. */
  readonly parent: string | null;
  /** Le frère devant lequel s'insérer ; nul pour se poser en dernier. */
  readonly avant: string | null;
};

/** Le parent d'un poste et ses frères, dans l'arbre affiché. */
function situer(
  racines: readonly NoeudPosable[],
  numero: string,
): { parent: string | null; fratrie: readonly NoeudPosable[] } | null {
  const chercher = (
    noeuds: readonly NoeudPosable[],
    parent: string | null,
  ): { parent: string | null; fratrie: readonly NoeudPosable[] } | null => {
    if (noeuds.some((n) => n.numero === numero)) return { parent, fratrie: noeuds };
    for (const noeud of noeuds) {
      const trouve = chercher(noeud.enfants ?? [], noeud.numero);
      if (trouve !== null) return trouve;
    }
    return null;
  };
  return chercher(racines, null);
}

/**
 * Traduit un dépôt en destination, ou rend `null` quand il n'a pas de sens.
 *
 * Deux refus, tous deux rendus ici plutôt qu'au serveur : déposer un bloc sur lui-même, et
 * le déposer dans son propre contenu. Le serveur les refuse aussi — c'est lui qui fait
 * autorité —, mais les laisser passer jusqu'à lui ferait clignoter un message d'erreur là
 * où l'écran doit simplement ne pas proposer le dépôt.
 */
export function poserLeBloc(
  racines: readonly NoeudPosable[],
  bloc: string,
  cible: string,
  moitie: MoitieSurvolee,
): Pose | null {
  if (bloc === cible) return null;

  const dansLeBloc = (noeuds: readonly NoeudPosable[]): boolean =>
    noeuds.some(
      (n) => n.numero === cible || dansLeBloc(n.enfants ?? []),
    );
  const situationDuBloc = situer(racines, bloc);
  if (situationDuBloc !== null) {
    const noeudBloc = situationDuBloc.fratrie.find((n) => n.numero === bloc);
    if (noeudBloc !== undefined && dansLeBloc(noeudBloc.enfants ?? [])) return null;
  }

  const situation = situer(racines, cible);
  if (situation === null) return null;
  const noeudCible = situation.fratrie.find((n) => n.numero === cible);
  if (noeudCible === undefined) return null;

  if (moitie === "haute") {
    // Juste avant la cible, à son niveau.
    return { parent: situation.parent, avant: cible };
  }

  if (noeudCible.nature === "chapitre") {
    // Dans le chapitre survolé, en premier — la seule position qui permette d'entrer
    // dans un chapitre encore vide, où il n'existe aucun frère à viser.
    const premier = (noeudCible.enfants ?? [])[0];
    return { parent: cible, avant: premier?.numero ?? null };
  }

  // Après la tâche survolée, à son niveau. `avant` désigne le frère suivant ; nul quand la
  // cible est la dernière, ce que le serveur lit comme « en dernier ».
  const rang = situation.fratrie.findIndex((n) => n.numero === cible);
  const suivant = situation.fratrie[rang + 1];
  return { parent: situation.parent, avant: suivant?.numero ?? null };
}
